import { useSession } from '@/lib/session';
import { Button, Dialog, DialogContent, DialogTitle, ScrollArea, cn } from '@iq/ui';
import {
  Settings as Cog,
  type LucideIcon,
  Shield,
  ShieldCheck,
  UserCircle2,
  Wand2,
  X,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountPanel } from './tabs/account-panel';
import { AdminPanel } from './tabs/admin-panel';
import { GeneralPanel } from './tabs/general-panel';
import { PersonalizationPanel } from './tabs/personalization-panel';
import { SecurityPanel } from './tabs/security-panel';

type TabKey = 'general' | 'personalization' | 'security' | 'account' | 'admin';

interface TabDef {
  key: TabKey;
  icon: LucideIcon;
  labelKey: string;
  panel: () => ReactNode;
  adminOnly?: boolean;
}

const TABS: TabDef[] = [
  { key: 'general', icon: Cog, labelKey: 'settings.tabs.general', panel: () => <GeneralPanel /> },
  {
    key: 'personalization',
    icon: Wand2,
    labelKey: 'settings.tabs.personalization',
    panel: () => <PersonalizationPanel />,
  },
  {
    key: 'security',
    icon: Shield,
    labelKey: 'settings.tabs.security',
    panel: () => <SecurityPanel />,
  },
  {
    key: 'account',
    icon: UserCircle2,
    labelKey: 'settings.tabs.account',
    panel: () => <AccountPanel />,
  },
  {
    key: 'admin',
    icon: ShieldCheck,
    labelKey: 'settings.tabs.admin',
    panel: () => <AdminPanel />,
    adminOnly: true,
  },
];

export interface SettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Settings dialog. Floating card on every viewport, horizontal scrollable tab
 * strip on mobile, vertical sidebar on tablet+.
 *
 * The shadcn `DialogContent` primitive is already `display: grid`, so layout
 * lives directly inside it (no nested wrapper) and overrides
 * `grid-template-rows` to explicit `[auto_auto_minmax(0,1fr)]` on mobile /
 * `[auto_minmax(0,1fr)]` on md+. The `minmax(0,1fr)` track gives the panel row
 * a finite upper bound so the inner `overflow-y-auto` computes a real
 * max-height and scrolls instead of pushing through the modal frame.
 */
export function SettingsModal({ open, onOpenChange }: SettingsModalProps) {
  const { t } = useTranslation();
  const session = useSession();
  const canOpenAdminPanel =
    session.status === 'authenticated' &&
    (session.session.role === 'admin' || session.session.role === 'auditor');
  const visibleTabs = TABS.filter((tab) => !tab.adminOnly || canOpenAdminPanel);
  const [active, setActive] = useState<TabKey>('general');
  const activeTab = visibleTabs.find((t) => t.key === active) ?? visibleTabs[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          // Floating card. Hide DialogContent's default X button; the
          // header/sidebar render their own.
          'gap-0 overflow-hidden p-0 [&>button]:hidden',
          // Mobile: centered card with ~1rem viewport margin.
          'h-[min(640px,calc(100dvh-2rem))] w-[calc(100vw-1rem)] max-w-md rounded-2xl',
          // Tablet+: wider, fixed 640px tall.
          'sm:h-[640px] sm:w-full sm:max-w-4xl',
          // Grid track sizing — see the file-level comment.
          'grid-rows-[auto_auto_minmax(0,1fr)]',
          'md:grid-cols-[220px_minmax(0,1fr)] md:grid-rows-[auto_minmax(0,1fr)]',
        )}
        data-testid="settings-modal"
      >
        <DialogTitle className="sr-only">{t('settings.title')}</DialogTitle>

        {/* Mobile header (md+ hidden — its grid cell collapses via display:none). */}
        <header className="flex items-center justify-between border-b px-3 py-2 md:hidden">
          <h2 className="text-base font-semibold">
            {activeTab ? t(activeTab.labelKey) : t('settings.title')}
          </h2>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            aria-label={t('settings.close')}
            data-testid="settings-close-mobile"
          >
            <X className="size-4" />
          </Button>
        </header>

        {/* Mobile tab strip (horizontal scroll). */}
        <nav
          className="flex shrink-0 gap-1 overflow-x-auto border-b px-2 py-2 md:hidden"
          aria-label={t('settings.title')}
          data-testid="settings-tabs-mobile"
        >
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const selected = tab.key === active;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActive(tab.key)}
                data-testid={`settings-tab-${tab.key}`}
                className={cn(
                  'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors',
                  selected
                    ? 'bg-accent font-medium text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                )}
              >
                <Icon className="size-4 shrink-0" />
                <span>{t(tab.labelKey)}</span>
              </button>
            );
          })}
        </nav>

        {/* Desktop sidebar — placed at col 1, spanning both rows. */}
        <aside className="hidden border-r bg-muted/30 p-3 md:flex md:flex-col md:gap-1 md:col-start-1 md:row-start-1 md:row-span-2">
          <div className="mb-2 flex justify-start">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              aria-label={t('settings.close')}
              data-testid="settings-close"
            >
              <X className="size-4" />
            </Button>
          </div>
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const selected = tab.key === active;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActive(tab.key)}
                data-testid={`settings-tab-desktop-${tab.key}`}
                className={cn(
                  'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                  selected
                    ? 'bg-accent font-medium text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                )}
              >
                <Icon className="size-4 shrink-0" />
                <span>{t(tab.labelKey)}</span>
              </button>
            );
          })}
        </aside>

        {/* Desktop header — col 2 row 1. */}
        <header className="hidden items-center border-b px-6 py-4 md:col-start-2 md:row-start-1 md:flex">
          <h2 className="text-base font-semibold">
            {activeTab ? t(activeTab.labelKey) : t('settings.title')}
          </h2>
        </header>

        {/* Scrollable panel area. On mobile sits at row 3 of the 3-row grid;
            on md+ jumps to col 2 row 2 of the 2x2 grid. The grid track is
            `minmax(0,1fr)` so `ScrollArea`'s viewport gets a finite ceiling.
            `type="always"` keeps the styled scrollbar visible (the default
            'hover' fades when idle, which masks scrollability). */}
        <ScrollArea
          type="always"
          className="min-h-0 md:col-start-2 md:row-start-2"
          data-testid="settings-panel-scroll"
        >
          <div className="px-4 py-4 sm:px-6 sm:py-5">{activeTab ? activeTab.panel() : null}</div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export interface SettingsTriggerProps {
  iconOnly?: boolean;
}

export function SettingsTrigger({ iconOnly = false }: SettingsTriggerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size={iconOnly ? 'icon' : 'sm'}
        onClick={() => setOpen(true)}
        data-testid="settings-trigger"
        aria-label={t('settings.title')}
      >
        <Cog className={iconOnly ? 'size-4' : 'mr-2 h-4 w-4'} />
        {!iconOnly ? <span>{t('settings.title')}</span> : null}
      </Button>
      <SettingsModal open={open} onOpenChange={setOpen} />
    </>
  );
}
